import { useState, useEffect, useCallback } from 'react';

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const API_BASE_URL = 'https://latest.currency-api.pages.dev/v1/currencies';

/**
 * Custom hook for currency conversion using FawazAhmed0 API.
 * Supports caching to minimize network requests.
 * 
 * @param {string} baseCurrency - The base currency code (default: 'usd')
 * @returns {Object} - { convert, rates, loading, error }
 */
const useCurrency = (baseCurrency = 'usd') => {
    const [rates, setRates] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRates = async () => {
            const lowerBase = baseCurrency.toLowerCase();
            const cacheKey = `nexora_rates_${lowerBase}`;

            try {
                // 1. Check Cache
                const cachedData = localStorage.getItem(cacheKey);
                if (cachedData) {
                    const { timestamp, data } = JSON.parse(cachedData);
                    if (Date.now() - timestamp < CACHE_DURATION) {
                        setRates(data);
                        setLoading(false);
                        return;
                    }
                }

                // 2. Fetch from API
                const response = await fetch(`${API_BASE_URL}/${lowerBase}.json`);
                if (!response.ok) {
                    throw new Error('Failed to fetch currency rates');
                }

                const json = await response.json();
                const newRates = json[lowerBase];

                // 3. Update State & Cache
                setRates(newRates);
                localStorage.setItem(cacheKey, JSON.stringify({
                    timestamp: Date.now(),
                    data: newRates
                }));
                setLoading(false);

            } catch (err) {
                console.error("Currency fetch error:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchRates();
    }, [baseCurrency]);

    /**
     * Convert an amount from the base currency to a target currency.
     * @param {number} amount - Amount to convert
     * @param {string} toCurrency - Target currency code (e.g., 'lkr', 'eur')
     * @returns {string|null} - Converted amount formatted to 2 decimals, or null if loading/error
     */
    const convert = useCallback((amount, toCurrency) => {
        if (!rates || Object.keys(rates).length === 0) return null;

        const targetRate = rates[toCurrency.toLowerCase()];
        if (!targetRate) {
            console.warn(`Currency ${toCurrency} not found in rates.`);
            return null;
        }

        return (amount * targetRate).toFixed(2);
    }, [rates]);

    /**
     * Get the formatted price string dynamically.
     * Useful for directly displaying "LKR 45,000.00"
     */
    const formatPrice = useCallback((amount, toCurrency, locale = 'en-US') => {
        const converted = convert(amount, toCurrency);
        if (!converted) return '...';

        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: toCurrency.toUpperCase(),
        }).format(converted);
    }, [convert]);

    return {
        rates,
        loading,
        error,
        convert,
        formatPrice
    };
};

export default useCurrency;
